/**
 * THE MANDATORY COMPANY SETTINGS — WHAT IS DONE, WHAT IS ELSEWHERE, WHAT IS NOT BUILT.
 * ===================================================================================
 *
 * THE OWNER'S FINDING 6, 2026-10-20, in his words: *"בכרטיס הלקוח מוצגים בעיקר מצב מנוי, מונים
 * והסברים ארוכים על השעיה, סגירה ושמירת מידע. לא מצאנו מסלול ברור להשלמת ההגדרות"*, with the
 * instruction *"הצג לכל דרישה אם היא קיימת, נמצאת במסך אחר או חסרה"*.
 *
 * So this panel is that classification, rendered, for the five fields the company-management
 * chapter (0.6 §4 and §14) makes mandatory to activate a company:
 *
 *     סוג ישות · מזהה רשמי · משתמש · כתובת מייל · מערכת הנהלת חשבונות
 *
 * ── THE THREE STATES, AND WHY THE THIRD IS NOT HIDDEN ───────────────────────────────────────────
 *
 *   `set`       the card can see it and it is there.
 *   `elsewhere` it is real and it is not this screen's to collect. The chapter is explicit about
 *               the user and the e-mail: *"אין לאסוף מחדש פרטי משתמש שכבר קיימים במנגנון הקנוני"*.
 *   `absent`    nothing in this product stores it yet. Shown, named, and NOT rendered as a thing
 *               the operator forgot — *"היעדרם מסומן ״טרם הוגדר״ ולא מתורגם ל״לא״ או לאפס"*.
 *
 * ── THE ACCOUNTING SYSTEM WAS THE `absent` ONE, AND IT IS NOT ANY MORE. **2026-10-21.** ─────────
 *
 * What this header used to say, and it was true when it was written: the chapter requires the
 * selection in the company screen — *"בחירת המערכת היא חובה במסך החברה"* — and the schema modelled
 * no company-to-system selection. `core.external_system` held the systems,
 * `core.company_external_identifier` held an identifier VALUE per system, and a company that had
 * chosen Priority with no Priority code yet was a state neither of them could express. Inventing a
 * column inside a defect fix would have been a product decision taken in passing, so it was
 * REPORTED here instead, with what it needed.
 *
 * The Owner then decided it, and it was built: migration `0020` adds
 * `core.company.source_system_id`, and `CompanyAccountingSystem` renders the control one block
 * below this panel. So the row now reads `elsewhere` and points at it.
 *
 * The `absent` state itself STAYS in the vocabulary. It was right once and will be right again, and
 * a panel that can only say "done" or "look over there" cannot report work that has not been built
 * — which is the thing this component exists to do.
 */
import type { JSX } from 'react';

export type SettingState = 'set' | 'elsewhere' | 'absent' | 'unread';

export interface CompanySettingsPanelProps {
  /** The official number as this screen's read returns it. `null` = the read carries none. */
  readonly registrationNumber: string | null;
  /**
   * The server's "this company's identity needs looking at" flag. Decided in ONE place --
   * `SqlCompanyStore`, from `legal_entity_id IS NULL` or `tax_id_status = 'disputed'` -- and never
   * re-derived here from the number's shape.
   */
  readonly needsAttention: boolean;
  /**
   * `core.company.entity_kind`, which the browser contract now declares. It was reported as
   * *"stored and not returned"* because this screen was given no way to name it; the field was on
   * the response the whole time.
   */
  readonly entityKind: string;
  /** Where the user and the e-mail come from, named so the operator is not sent looking. */
  readonly identityScreenHe: string;
}

interface Requirement {
  readonly labelHe: string;
  readonly state: SettingState;
  readonly whatHe: string;
}

const MARK: Readonly<Record<SettingState, string>> = {
  set: '\u2713',
  elsewhere: '\u2192',
  absent: '\u2022',
  unread: '?',
};

/**
 * THE OFFICIAL NUMBER, from what this screen is actually given.
 *
 * `CompanySummary.registrationNumber` is the number itself and `needsAttention` is the server's own
 * broken-identity flag (09 section 5.1). Nothing is derived from the number's SHAPE here: the check
 * digit is the server's single verification, and a second opinion in the browser is how two rules
 * come to disagree about nine digits.
 */
function officialNumber(registrationNumber: string | null, needsAttention: boolean): Requirement {
  /*
   * `typeof !== 'string'` AND NOT `=== null`. **The browser crash of 2026-10-25.**
   *
   * This read `registrationNumber === null || registrationNumber.trim() === ''`. The declared type
   * is `string | null`, and what actually arrived was `undefined`: the field was declared in the
   * browser contract and produced by no server code, so it was simply absent from the response.
   * `undefined === null` is false, `.trim()` threw, and the whole company card was replaced by the
   * render-fault screen -- with no request having been made, which is what that screen said.
   *
   * The data defect is fixed at the source. This guard stays written this way regardless: a panel
   * whose entire job is to report that a setting is missing must survive the setting being missing
   * in a way its type did not anticipate.
   */
  if (typeof registrationNumber !== 'string' || registrationNumber.trim() === '') {
    return {
      labelHe: 'מזהה רשמי',
      state: 'absent',
      whatHe:
        'המערכת אינה מחזיקה מספר רשמי לחברה הזאת. חברה שהוקמה לפני עדכון זה עשויה להיות במצב הזה — ' +
        'המספר נדרש כדי להשלים את הגדרות החברה, ומאז העדכון הוא שדה חובה בהקמה.',
    };
  }
  if (needsAttention) {
    return {
      labelHe: 'מזהה רשמי',
      state: 'absent',
      whatHe: `המספר ${registrationNumber} נשמר, והשרת מסמן את זהות החברה כדורשת טיפול.`,
    };
  }
  return {
    labelHe: 'מזהה רשמי',
    state: 'set',
    whatHe: `${registrationNumber} — נשמר, וספרת הביקורת שלו נבדקה בעת ההזנה.`,
  };
}

export function CompanySettingsPanel({
  registrationNumber,
  needsAttention,
  entityKind,
  identityScreenHe,
}: CompanySettingsPanelProps): JSX.Element {
  const requirements: readonly Requirement[] = [
    /*
     * IT WAS `unread`, AND THE READING GAP IS CLOSED. **2026-10-25.**
     *
     * The old row said: *"stored at creation, and this screen does not read it from the server"*.
     * That was true of the BROWSER CONTRACT rather than of the response -- `entity_kind` was in
     * every company answer and `CompanySummary` did not declare it, so nothing could name it and
     * the edit form below started blank. The contract declares it now.
     *
     * `unread` stays in the vocabulary. It was right until this round and will be right again.
     */
    entityKind.trim() === ''
      ? {
          labelHe: 'סוג ישות',
          state: 'absent',
          whatHe:
            'השרת החזיר סוג ישות ריק. העמודה היא NOT NULL בסכמה, ולכן זהו מצב שאין לו ' +
            'הסבר במסך הזה — יש לפנות לתמיכה.',
        }
      : {
          labelHe: 'סוג ישות',
          state: 'set',
          whatHe: `${entityKind} — נקבע בהקמה ונשמר.`,
        },
    officialNumber(registrationNumber, needsAttention),
    {
      labelHe: 'משתמש',
      state: 'elsewhere',
      whatHe: `מנוהל במנגנון הזהויות של הארגון — ${identityScreenHe}. אינו נאסף מחדש בכרטיס החברה.`,
    },
    {
      labelHe: 'כתובת מייל',
      state: 'elsewhere',
      whatHe: `מגיעה מאותה זהות — ${identityScreenHe}. אין עותק שלה בכרטיס החברה.`,
    },
    {
      labelHe: 'מערכת הנהלת חשבונות',
      /*
       * `elsewhere` ON THE SAME SCREEN, AND THAT IS THE HONEST STATE.
       *
       * It was `absent` -- *"אין לה כרגע מקום שמור במערכת"* -- and that stopped being true on
       * 2026-10-21: migration `0020` added `core.company.source_system_id`, and the control sits
       * DIRECTLY BELOW this panel (`CompanyAccountingSystem`).
       *
       * It is not `set`, because this panel is given no fact about it: `CompanySummary` carries no
       * source system, so claiming a value here would be a fabrication. It is not `unread` either,
       * which means *"stored but not returned by this read"* -- the field is read, by the control,
       * from its own route. So the row points at where the answer is, which is what `elsewhere`
       * means, and the panel stops naming it as missing work.
       */
      state: 'elsewhere',
      whatHe:
        'נבחרת ומוצגת בשדה ״מערכת הנהלת חשבונות״ שמתחת לפאנל הזה. הבחירה היא הצהרה על מקום הספרים ' +
        'ואינה חיבור — חיבור בפועל נעשה במסך ״מקורות נתונים״.',
    },
  ];

  const missing = requirements.filter((requirement) => requirement.state === 'absent');
  const unread = requirements.filter((requirement) => requirement.state === 'unread');

  return (
    <section
      className="notice"
      data-tone={missing.length === 0 ? 'info' : 'warn'}
      aria-label="הגדרות החובה של החברה"
    >
      <h2 className="notice__title">
        {missing.length === 0
          ? 'הגדרות החובה של החברה'
          : `הגדרות החובה של החברה — חסר: ${missing.map((row) => row.labelHe).join(', ')}`}
      </h2>
      <ul className="notice__body">
        {requirements.map((requirement) => (
          <li key={requirement.labelHe}>
            <span dir="ltr">{MARK[requirement.state]}</span> <strong>{requirement.labelHe}</strong> —{' '}
            {requirement.whatHe}
          </li>
        ))}
      </ul>
      <p className="notice__body">
        שאר הגדרות החברה אינן תנאי להפעלה. היעדר הגדרה מוצג כ״טרם הוגדר״ ואינו נקרא כ״לא״ או כאפס.
        {unread.length === 0 ? '' : ' סימן שאלה מציין נתון שקיים בשרת ואינו נקרא במסך הזה.'}
      </p>
    </section>
  );
}
